import { createContext, useContext, useEffect, useState, useCallback } from "react";

const ThemeContext = createContext(null);
const STORAGE_KEY = "carat-ledger-theme";
const ACCENT_KEY = "carat-ledger-accent";
const FONT_KEY = "carat-ledger-fontscale";
export const FONT_STEPS = [90, 100, 110, 125];

// Keep the user's percentage relative to the same responsive base used by
// index.css. Previously 100% removed the inline value, allowing a 17-21px
// monitor rule to win, while 110% meant 17.6px; on large screens 110% could
// therefore look smaller than 100%.
const responsiveRootPx = (width) => {
  if (width >= 3200) return 21;
  if (width >= 2560) return 19;
  if (width >= 2200) return 18;
  if (width >= 1800) return 17;
  return 16;
};

// Per-user accent presets — saturated mid-tones that read well with white text.
export const ACCENT_PRESETS = [
  { key: "blue", label: "Sky", color: "#0284C7", hover: "#0369A1" },
  { key: "indigo", label: "Indigo", color: "#4F46E5", hover: "#4338CA" },
  { key: "violet", label: "Violet", color: "#7C3AED", hover: "#6D28D9" },
  { key: "pink", label: "Pink", color: "#DB2777", hover: "#BE185D" },
  { key: "rose", label: "Rose", color: "#E11D48", hover: "#BE123C" },
  { key: "amber", label: "Amber", color: "#D97706", hover: "#B45309" },
  { key: "emerald", label: "Emerald", color: "#059669", hover: "#047857" },
  { key: "teal", label: "Teal", color: "#0D9488", hover: "#0F766E" },
  { key: "slate", label: "Graphite", color: "#334155", hover: "#1E293B" },
];

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(STORAGE_KEY) || "light";
  });
  const [accent, setAccentState] = useState(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem(ACCENT_KEY) || ""; // "" = theme default (no override)
  });
  const [fontScale, setFontScaleState] = useState(() => {
    if (typeof window === "undefined") return 100;
    const v = parseInt(localStorage.getItem(FONT_KEY), 10);
    return FONT_STEPS.includes(v) ? v : 100;
  });

  useEffect(() => {
    const applyFontScale = () => {
      const base = responsiveRootPx(window.innerWidth);
      document.documentElement.style.fontSize = `${base * (fontScale / 100)}px`;
    };
    applyFontScale();
    window.addEventListener("resize", applyFontScale);
    // Virtualized tables derive their row metrics from the root font size.
    window.dispatchEvent(new Event("resize"));
    localStorage.setItem(FONT_KEY, String(fontScale));
    return () => window.removeEventListener("resize", applyFontScale);
  }, [fontScale]);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "light") root.classList.add("light");
    else root.classList.remove("light");
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  useEffect(() => {
    const root = document.documentElement;
    const preset = ACCENT_PRESETS.find((p) => p.key === accent);
    if (preset) {
      root.dataset.accent = preset.key;
      root.style.setProperty("--user-accent", preset.color);
      root.style.setProperty("--accent-hover", preset.hover);
      root.style.setProperty("--accent-contrast", "#ffffff");
    } else {
      delete root.dataset.accent;
      root.style.removeProperty("--user-accent");
      root.style.removeProperty("--accent-hover");
    }
    if (accent) localStorage.setItem(ACCENT_KEY, accent);
    else localStorage.removeItem(ACCENT_KEY);
  }, [accent]);

  const toggle = useCallback(() => {
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  }, []);
  const setAccent = useCallback((key) => setAccentState(key || ""), []);
  const setFontScale = useCallback((v) => {
    const n = parseInt(v, 10);
    setFontScaleState(FONT_STEPS.includes(n) ? n : 100);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggle, setTheme, accent, setAccent, fontScale, setFontScale }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
